import { CategoryService } from '@core/services/steelfab';
import { Component, DefineDependencies } from '@rhtml/component';
import { AngularLayout } from '@rhtml/modifiers';
import { Container } from '@rxdi/core';
import { deepCopy } from '@rxdi/graphql-client';
import { css, html, LitElement, state } from '@rxdi/lit-html';
import { Router } from '@rxdi/router';
import { SteelFabTheme } from '@shared/styles/theme';

/**
 * @customElement categories-component
 */
@Component({
  Settings: {
    selector: 'categories-component',
    styles: [
      css`
        ${SteelFabTheme}

        :host {
          display: block;
        }

        .section {
          padding: 90px 0;
          background: var(--sf-bg-primary);
        }

        .section-badge {
          display: inline-block;
          background: var(--sf-bg-secondary);
          color: var(--sf-navy);
          border: 1px solid var(--sf-bg-tertiary);
          font-size: var(--sf-font-size-sm);
          font-weight: 600;
          padding: 6px 16px;
          border-radius: var(--sf-radius-pill);
          margin-bottom: 1rem;
          letter-spacing: 0.05em;
          text-transform: uppercase;
        }

        .section-title {
          font-size: var(--sf-font-size-4xl);
          font-weight: 700;
          color: var(--sf-text-primary);
          margin: 0 0 1rem;
          font-family: var(--sf-font-sans);
          letter-spacing: -0.02em;
        }

        .section-subtitle {
          font-size: var(--sf-font-size-lg);
          color: var(--sf-text-secondary);
          margin: 0 0 3.5rem;
          max-width: 540px;
          margin-left: auto;
          margin-right: auto;
          line-height: 1.6;
        }

        .section-header {
          text-align: center;
          margin-bottom: 3rem;
        }

        .grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1.5rem;
        }

        .category-card {
          background: var(--sf-bg-primary);
          border: 1px solid var(--sf-border-light);
          border-radius: var(--sf-radius-md);
          padding: 2rem 1.5rem;
          text-align: center;
          cursor: pointer;
          position: relative;
          overflow: hidden;
          transition: all 0.3s ease;
          box-shadow: var(--sf-shadow-sm);
        }

        .category-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: var(--sf-gradient-accent);
          transform: scaleX(0);
          transition: transform 0.3s ease;
          transform-origin: left;
        }

        .category-card:hover {
          transform: translateY(-6px);
          box-shadow: var(--sf-shadow-lg);
          border-color: var(--sf-accent-light);
        }

        .category-card:hover::before {
          transform: scaleX(1);
        }

        .category-icon {
          width: 72px;
          height: 72px;
          margin: 0 auto 1.25rem;
          background: var(--sf-bg-secondary);
          border-radius: var(--sf-radius-md);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 2rem;
          border: 1px solid var(--sf-border-light);
          transition: all 0.3s ease;
        }

        .category-card:hover .category-icon {
          background: var(--sf-gradient-primary);
          border-color: transparent;
        }

        .category-icon span {
          font-size: 1.75rem;
          filter: grayscale(0.2);
          transition: filter 0.3s ease;
        }

        .category-card:hover .category-icon span {
          filter: grayscale(0) brightness(1.2);
        }

        .category-name {
          font-size: var(--sf-font-size-base);
          font-weight: 700;
          color: var(--sf-text-primary);
          margin: 0 0 0.5rem;
          font-family: var(--sf-font-sans);
          line-height: 1.3;
          transition: color 0.2s ease;
        }

        .category-card:hover .category-name {
          color: var(--sf-navy);
        }

        .category-desc {
          font-size: var(--sf-font-size-sm);
          color: var(--sf-text-muted);
          line-height: 1.5;
          margin: 0;
        }

        .arrow {
          display: inline-block;
          margin-top: 1rem;
          font-size: var(--sf-font-size-sm);
          color: var(--sf-steel);
          font-weight: 600;
          opacity: 0;
          transform: translateX(-4px);
          transition: all 0.25s ease;
        }

        .category-card:hover .arrow {
          opacity: 1;
          transform: translateX(0);
        }

        @media (max-width: 768px) {
          .grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 1rem;
          }
          .section {
            padding: 60px 0;
          }
          .section-title {
            font-size: var(--sf-font-size-3xl);
          }
        }

        @media (max-width: 480px) {
          .grid {
            grid-template-columns: 1fr;
          }
        }
      `,
    ],
    modifiers: [...AngularLayout],
  },
  Providers: DefineDependencies(CategoryService)(Container),
  State(this: CategoriesComponent, [categoryService]) {
    return categoryService.getCategoryTree();
  },
  Render: () =>
    function (this: CategoriesComponent, state) {
      const categories = deepCopy(state);
      return html`
        <section class="section" id="categories-component">
          <div class="container">
            <div class="section-header">
              <div class="section-badge">Нашите продукти</div>
              <h2 class="section-title">Категории Машини</h2>
              <p class="section-subtitle">
                Предлагаме широка гама от металообработващо и индустриално оборудване за всички производствени нужди.
              </p>
            </div>

            <div class="grid">
              ${categories.map(
                (cat) => html`
                  <div class="category-card" @click=${() => this.goto(cat.slug)}>
                    <div class="category-icon">
                      <img ngIf=${cat.logo?.url} style="width: 100px; height: 100px;" src=${cat.logo?.url} />
                      <span ngIf=${!cat.logo?.url}>${cat.icon}</span>
                    </div>
                    <h3 class="category-name">${cat.name}</h3>
                    <p class="category-desc">${cat.description}</p>
                    <div class="arrow">Виж продуктите →</div>
                  </div>
                `
              )}
            </div>
          </div>
        </section>
      `;
    },
})
export class CategoriesComponent extends LitElement {
  @Router()
  private router: Router;

  private goto(slug: string) {
    this.router.go(`/products/${slug}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}
